import { dayOf, endOf, startOf } from './adapters';
import { toDateInputValue } from '@/shared/lib/dates';
import type { AgendaOccurrence, CalendarItem, ProjectRowView } from '@/shared/types/domain';

/**
 * Os itens de UM dia (spec-calendar.md §5, decisões 71 e 72).
 *
 * **Um projeto de vários dias pertence a todos os dias que ele atravessa**, e
 * não só ao de início. Um retiro de sexta a domingo tem de aparecer na lista do
 * sábado: quem clica no sábado está perguntando *o que acontece neste dia*, e a
 * resposta "nada" seria falsa com o Templo ocupado.
 *
 * A comparação é de DIA CIVIL no fuso da igreja, nunca de instante — o retiro
 * que termina domingo às 12h ainda é do domingo, e um `<=` sobre timestamps
 * responderia isso errado por meia hora todo horário de verão.
 */
export function itemsOfDay(
  items: readonly CalendarItem[],
  day: string,
): readonly CalendarItem[] {
  return items.filter((item) => {
    const first = dayOf(item);
    const end = endOf(item);
    const last = end ? toDateInputValue(end) : first;
    return first <= day && day <= last;
  });
}

/**
 * As duas populações que a lista do dia consome, separadas — porque `ProjectTable`
 * recebe projeto e ocorrência em props diferentes (§5.1).
 *
 * Ordenadas por horário de início já aqui: a mesclagem final (`mergeDayRows`, em
 * `features/projects`) é estável, então a ordem que sai daqui é a que a tabela
 * mostra.
 */
export function splitDayItems(items: readonly CalendarItem[]): {
  projects: readonly ProjectRowView[];
  occurrences: readonly AgendaOccurrence[];
} {
  const sorted = [...items].sort((a, b) => Date.parse(startOf(a)) - Date.parse(startOf(b)));

  return {
    projects: sorted.flatMap((item) => (item.kind === 'project' ? [item.project] : [])),
    occurrences: sorted.flatMap((item) => (item.kind === 'occurrence' ? [item.occurrence] : [])),
  };
}
